import { useEffect, useRef } from "react";
import { API } from "../context";

/**
 * App-wide per-user notification socket. Connects once while signed in and
 * fires a window `iw:social-event` whenever the backend pushes a new
 * notification (message, voice, friend request/accept, reaction, …), so the
 * bell, the toast notifier and the push-to-talk popup update INSTANTLY instead
 * of waiting for their poll timers. Auto-reconnects; polling stays as a
 * fallback, so this only ever makes delivery faster.
 */
export default function useUserNotifySocket(uid) {
  const wsRef = useRef(null);
  const retryRef = useRef(null);
  const aliveRef = useRef(true);
  const pingRef = useRef(null);

  useEffect(() => {
    if (!uid) return undefined;
    aliveRef.current = true;

    const connect = () => {
      if (!aliveRef.current) return;
      const wsBase = API.replace(/^http/, "ws");
      let ws;
      try {
        ws = new WebSocket(`${wsBase}/friends/ws/notify/${encodeURIComponent(uid)}`);
      } catch {
        schedule();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        // Keepalive so proxies don't drop the idle socket.
        if (pingRef.current) clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          try { ws.readyState === WebSocket.OPEN && ws.send("ping"); } catch { /* ignore */ }
        }, 25000);
      };
      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          if (d && d.type === "notification") {
            window.dispatchEvent(new CustomEvent("iw:social-event", { detail: d.notification || null }));
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { if (pingRef.current) clearInterval(pingRef.current); schedule(); };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };

    const schedule = () => {
      if (!aliveRef.current) return;
      if (retryRef.current) clearTimeout(retryRef.current);
      retryRef.current = setTimeout(connect, 2500);
    };

    connect();
    return () => {
      aliveRef.current = false;
      if (retryRef.current) clearTimeout(retryRef.current);
      if (pingRef.current) clearInterval(pingRef.current);
      try { wsRef.current && wsRef.current.close(); } catch { /* ignore */ }
      wsRef.current = null;
    };
  }, [uid]);
}
